import React, { Fragment, useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import * as DateFns from "date-fns";

import Table, { RowType } from "../../Shared/CustomTable";
import DraggableTable from "../../Shared/CustomTable/DraggableTable/DraggableTable";
import EditableField from "../../Shared/EditableField";
import DrillProgress from "./DrillProgress";
import EditableTime from "./EditableTime";
import ElapsedTime from "./ElapsedTime";

import {
  msToTime,
  findMex,
  cleanControllerSet,
  text_truncate,
} from "../../../helpers/helpers";
import { periodsTableTimeValidator } from "../../../helpers/validators";
import {
  FINISH,
  CONTROLLER_SET,
  PERIOD_END_LABEL,
  PERIOD_START_LABEL,
  PAST_PRACTICE,
  FIELD_TYPE,
} from "../../../config/constants";
import {
  PeriodTypeInlineMultiSelector,
  PeriodTypeInlineMultiSelectorItemType,
} from "../../Shared/Filters/PeriodTypeInlineMultiSelector";
import { useDataEventsState, usePracticeDataUpdateDispatch } from "store/hooks";
import { useManageDispatch } from "store/hooks/manage";
import { Drill, Manage } from "config/commonTypes";
import PeriodsTableMobile from "./PeriodsTableMobile";
import Loader from "components/Shared/Loader";
import { Dropdown } from "react-bootstrap";

type PeriodsTableProps = {
  currentPractice: Manage;
  setCurrentPractice: React.Dispatch<React.SetStateAction<Manage>>;
  editable: boolean;
  shouldSave: boolean;
  updateQueue: { queue: any[]; fetching: number };
  periodsValidation: React.SetStateAction<{ startTs: {}; endTs: {} }>;
  setPeriodsValidation: React.Dispatch<
    React.SetStateAction<{ startTs: {}; endTs: {} }>
  >;
  setIsInProgressPeriod: React.Dispatch<React.SetStateAction<boolean>>;
  onOpenExcludePlayersModal: (sessionId: number, periodName: string) => void;
  participantsByPeriod: Array<{
    included: number;
    total: number;
    all: boolean;
  }>;
};
const PeriodsTable = ({
  currentPractice,
  setCurrentPractice,
  editable,
  updateQueue, // props
  periodsValidation,
  setPeriodsValidation,
  setIsInProgressPeriod,
  shouldSave, // props
  onOpenExcludePlayersModal,
  participantsByPeriod,
}: PeriodsTableProps) => {
  const { data: dataManage, meta } = useDataEventsState();
  const isPastPractice = meta?.type === PAST_PRACTICE;
  const practice = dataManage.manage;
  const { endPeriod, startPeriod, startPractice, startPracticeMobile } =
    useManageDispatch();
  const { noSave } = usePracticeDataUpdateDispatch();
  const currentDrillIndex =
    practice && practice.currentDrillIndex ? practice.currentDrillIndex : 0;
  const [started, setStarted] = useState(false);
  const [ended, setEnded] = useState(false);
  let periodOpen = 0;
  const isMobile = window.matchMedia(
    "only screen and (max-width: 900px)"
  ).matches;
  const handlePeriodNameChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    drillId: number
  ) => {
    noSave(true);
    const newPractice = JSON.parse(JSON.stringify(currentPractice));
    newPractice.drills = newPractice.drills.map(
      (drill: Drill, index: number) => {
        if (drill.drillId === drillId) {
          // edit the currentDrill name too!
          if (index === newPractice.currentDrillIndex) {
            newPractice.currentDrill = event.target.value;
          }
          drill.drillType = event.target.value; // then edit the period name
        }
        return drill;
      }
    );
    setCurrentPractice(newPractice);
  };

  const handlePeriodRoofType = (event: any, drillId: number | undefined) => {
    noSave(true);
    const newPractice = JSON.parse(JSON.stringify(currentPractice));
    newPractice.drills = newPractice.drills.map(
      (drill: Drill, index: number) => {
        if (drill.drillId === drillId) {
          drill.roofType = event; // then edit the period name
        }
        return drill;
      }
    );
    setCurrentPractice(newPractice);
  };
  const handlePeriodTypesChange = (
    values: PeriodTypeInlineMultiSelectorItemType[],
    drillId: number
  ) => {
    noSave(true);
    const newPractice = JSON.parse(JSON.stringify(currentPractice));
    newPractice.drills = newPractice.drills.map((drill: Drill) => {
      if (drill.drillId === drillId) {
        drill.periodType = values.join(",");
      }
      return drill;
    });
    setCurrentPractice(newPractice);
  };

  const correctHour = (am: boolean, hour: number) => {
    if (am === true) {
      if (hour === 12) {
        hour = 0;
      }
    } else {
      if (hour !== 12) {
        hour += 12;
      }
    }
    return hour;
  };

  const handlePeriodsTimeChange = (
    key: string,
    hour: number,
    minute: number,
    second: number,
    am: boolean,
    index?: number
  ) => {
    noSave(true);
    hour = correctHour(am, hour);
    let inputIndex;
    if (!index) {
      inputIndex = 0;
    } else {
      inputIndex = index;
    }
    let newTime = currentPractice.drills[index as number][key];
    newTime = DateFns.setHours(newTime, hour || 0);
    newTime = DateFns.setMinutes(newTime, minute || 0);
    newTime = DateFns.setSeconds(newTime, second || 0);
    if (inputIndex !== 0 && key === "startTs") {
      //CHEKS PREV DRILL END TIME MILLISEC
      const prevDrillIndex = inputIndex - 1;
      const prevDrillendTsStr =
        currentPractice.drills[prevDrillIndex].endTs.toString();
      const prevDrillMillisec =
        prevDrillendTsStr.charAt(prevDrillendTsStr.length - 3).toString() +
        prevDrillendTsStr.charAt(prevDrillendTsStr.length - 2).toString() +
        prevDrillendTsStr.charAt(prevDrillendTsStr.length - 1).toString();
      const checkDrillsTs =
        currentPractice.drills[prevDrillIndex].endTs - newTime.getTime();
      if (checkDrillsTs >= 0) {
        if (Number(prevDrillMillisec) >= 900) {
          const millisec = Number(prevDrillMillisec);
          newTime = DateFns.setMilliseconds(newTime, millisec || 0);
        } else {
          const millisec = Number(prevDrillMillisec) + 100;
          newTime = DateFns.setMilliseconds(newTime, millisec || 0);
        }
      } //CHECKS NEXT DRILL START TIME MILLISEC
    } else if (
      inputIndex !== currentPractice.drills.length - 1 &&
      currentPractice.drills[inputIndex + 1].startTs
    ) {
      const nextDrillIndex = inputIndex + 1;
      const nextDrillstartTsStr =
        currentPractice.drills[nextDrillIndex].startTs.toString();
      const nextDrillMillisec =
        nextDrillstartTsStr.charAt(nextDrillstartTsStr.length - 3).toString() +
        nextDrillstartTsStr.charAt(nextDrillstartTsStr.length - 2).toString() +
        nextDrillstartTsStr.charAt(nextDrillstartTsStr.length - 1).toString();
      const checkDrillsTs =
        newTime.getTime() - currentPractice.drills[nextDrillIndex].startTs;
      if (checkDrillsTs >= 0) {
        if (Number(nextDrillMillisec) <= 100) {
          const millisec = Number(nextDrillMillisec);
          newTime = DateFns.setMilliseconds(newTime, millisec || 0);
        } else {
          const millisec = Number(nextDrillMillisec) - 100;
          newTime = DateFns.setMilliseconds(newTime, millisec || 0);
        }
      }
    }
    //CHECKS CURRENT DRILL END AND START TS MILLISEC
    const startTsCheck =
      currentPractice.drills[inputIndex].endTs - newTime.getTime();
    const endTsCheck =
      newTime.getTime() - currentPractice.drills[inputIndex].startTs;
    const unmutedPractice = { ...currentPractice };
    unmutedPractice.drills[index as number][key] = newTime.getTime();
    if (startTsCheck < 0 && startTsCheck > -1000) {
      if (key === "startTs")
        unmutedPractice.drills[index as number]["endTs"] = newTime.getTime();
    }
    if (endTsCheck < 0 && endTsCheck > -1000) {
      if (key === "endTs")
        unmutedPractice.drills[index as number]["startTs"] = newTime.getTime();
    }
    setCurrentPractice(unmutedPractice);
    validatePeriodsTimeChange(index as number, unmutedPractice);
  };

  /**
   * Validation of the new inputed field, where it validates the opposite field (e.g. if it is
   * startTs it validates its endTs too!). And also the previous and next period's fields, that's
   * why the filter method is called, and the *for* loop evaluates all of 3 period's fields.
   * @param {number} index index of the period being edited
   */
  const validatePeriodsTimeChange = (index: number, updatedPeriods: Manage) => {
    const minIndex = index > 0 ? index - 1 : 0;
    const unmutedValidation: any = { ...periodsValidation };

    const valDrills =
      updatedPeriods.drills.filter((drill, ind) => {
        return minIndex <= ind && ind <= index + 1 && drill.startTs;
      }) || [];

    for (let i = 0; i < valDrills.length; i++) {
      const args: [number, any, Manage] = [
        minIndex + i,
        unmutedValidation,
        updatedPeriods,
      ];
      unmutedValidation[PERIOD_START_LABEL] = validateTimeChanges(
        PERIOD_START_LABEL,
        ...args
      );
      unmutedValidation[PERIOD_END_LABEL] = validateTimeChanges(
        PERIOD_END_LABEL,
        ...args
      );
    }

    setPeriodsValidation(unmutedValidation);
  };

  const validateTimeChanges = (
    key: string,
    index: number,
    unmutedValidation: any,
    updatedPeriods: Manage
  ) => {
    const labeledPeriod = { ...unmutedValidation[key] };
    const periodVal = periodsTableTimeValidator(key, index, updatedPeriods);

    if (periodVal) {
      labeledPeriod[index] = periodVal;
    } else if (labeledPeriod[index]) {
      delete labeledPeriod[index];
    }

    return labeledPeriod;
  };

  const handleDeletePeriod = (index: number) => {
    noSave(true);
    const newPractice = { ...currentPractice };
    newPractice.drills = newPractice.drills.filter((d, i) => i !== index);
    newPractice.drills.forEach((drill, index) => {
      if (drill.state === "FINISH" || drill.state === "START") {
        drill.session_id = index + 1;
      }
    });
    if (index < newPractice.currentDrillIndex) {
      newPractice.currentDrillIndex--;
    }
    setCurrentPractice(newPractice);
    validatePeriodsTimeChange(index, newPractice);
  };

  const handleAddPeriod = () => {
    noSave(true);
    const newPractice = JSON.parse(JSON.stringify(currentPractice));
    const drillId = findMex(
      (newPractice &&
        newPractice.drills &&
        newPractice.drills.map((d: Drill) => d.drillId)) ||
        []
    );
    let timeStamp = new Date();
    timeStamp = DateFns.setYear(
      timeStamp,
      DateFns.getYear(currentPractice.start)
    );
    timeStamp = DateFns.setMonth(
      timeStamp,
      DateFns.getMonth(currentPractice.start)
    );
    timeStamp = DateFns.setDate(
      timeStamp,
      DateFns.getDate(currentPractice.start)
    );
    if (isPastPractice) {
      timeStamp = DateFns.setHours(
        timeStamp,
        DateFns.getHours(
          currentPractice.drills.length > 0
            ? currentPractice.drills[currentPractice.drills.length - 1].endTs
            : currentPractice.start
        )
      );
      timeStamp = DateFns.setMinutes(
        timeStamp,
        DateFns.getMinutes(
          currentPractice.drills.length > 0
            ? currentPractice.drills[currentPractice.drills.length - 1].endTs
            : currentPractice.start
        )
      );
      timeStamp = DateFns.setSeconds(
        timeStamp,
        DateFns.getSeconds(
          currentPractice.drills.length > 0
            ? currentPractice.drills[currentPractice.drills.length - 1].endTs
            : currentPractice.start
        )
      );
    }
    if (!newPractice.drills) {
      newPractice.drills = [];
    }
    newPractice.drills.push(
      isPastPractice
        ? {
            drillType: "",
            drillId,
            session_id: newPractice.drills.length + 1,
            startTs: timeStamp.getTime(),
            endTs: timeStamp.getTime(),
            state: FINISH,
          }
        : { drillType: "", drillId }
    );
    if (isPastPractice) {
      newPractice.currentDrillIndex = newPractice.drills.length;
    }
    setCurrentPractice(newPractice);
  };

  const handleStartPeriod = (index = 0) => {
    cleanControllerSet(CONTROLLER_SET);
    //cleanArrayControllers(MANAGE_TAB_CONTROLLERS);
    if (!practice.currentDrillIndex || practice.currentDrillIndex === 0) {
      startPractice(JSON.parse(JSON.stringify(currentPractice)));
    } else {
      startPeriod(JSON.parse(JSON.stringify(currentPractice)), index);
    }
  };

  const handleStartPeriodMobile = (index = 0) => {
    cleanControllerSet(CONTROLLER_SET);
    //cleanArrayControllers(MANAGE_TAB_CONTROLLERS);
    if (!practice.currentDrillIndex || practice.currentDrillIndex === 0) {
      startPracticeMobile(JSON.parse(JSON.stringify(currentPractice)));
    } else {
      startPeriod(
        JSON.parse(JSON.stringify(currentPractice)),
        index,
        undefined,
        false,
        isMobile
      );
    }
  };

  const handleEndPeriod = (index: number, skipped: boolean) => {
    cleanControllerSet(CONTROLLER_SET);
    //cleanArrayControllers(MANAGE_TAB_CONTROLLERS);
    endPeriod(practice, index, skipped, isMobile);
    if (skipped) {
      isInProgress = true;
      setEnded(false);
    }
  };
  const headers = [
    {
      content: "NUMBER",
      style: { width: "65px", padding: "14px 10px" },
      class: "t-ll",
    },
    {
      content: "PERIOD TYPE",
      style: { width: "171px", padding: "14px 10px" },
      class: "t-ll",
    },
    {
      content: "PERIOD NAME",
      style: { width: "122px", padding: "14px 10px" },
      class: "t-ll",
    },
    {
      content: "FIELD TYPE",
      style: { width: "125px", padding: "14px 10px" },
      class: "t-ll",
    },
    {
      content: "START TIME",
      style: { width: "115px", padding: "14px 10px" },
      class: "t-ll",
    },
    {
      content: "END TIME",
      style: { width: "115px", padding: "14px 10px" },
      class: "t-ll",
    },
    {
      content: "ELAPSED",
      style: { width: "100px", padding: "14px 10px" },
      class: "t-ll",
    },
    {
      content: "PARTICIPANTS",
      style: { width: "114px", padding: "14px 10px" },
      class: "t-ll",
    },
    { content: "PROGRESS", style: { width: "346px", padding: "14px 10px" } },
  ];
  const onChangeRows = (indexA: number, indexB: number) => {
    noSave(true);
    const newPractice = JSON.parse(JSON.stringify(currentPractice));
    const newDrills = [...newPractice.drills];
    const movedDrill = newDrills[indexA];
    newDrills.splice(indexA, 1);
    newDrills.splice(indexB, 0, movedDrill);
    setCurrentPractice({ ...newPractice, drills: newDrills });
  };

  const getTableEllapsedData = (drill: Drill, index: number) => {
    const startTimestampsMessages = {
      ...periodsValidation[PERIOD_START_LABEL],
    };
    const endTimestampsMessages = { ...periodsValidation[PERIOD_END_LABEL] };
    if (startTimestampsMessages[index] || endTimestampsMessages[index]) {
      return (
        <i
          className="mdi mdi-alert"
          style={
            isMobile
              ? { color: "#D6151C", justifyContent: "center", fontSize: 18 }
              : {
                  display: "flex",
                  color: "#D6151C",
                  justifyContent: "center",
                  fontSize: 18,
                }
          }
        />
      );
    }
    return drill.state === "START" ? (
      <ElapsedTime drill={drill} />
    ) : drill.startTs && drill.endTs ? (
      drill.endTs >= drill.startTs ? (
        msToTime(drill.endTs - drill.startTs)
      ) : (
        "-"
      )
    ) : (
      ""
    );
  };
  const labelsMobile = [
    { content: "PERIOD TYPE" },
    { content: "START TIME" },
    { content: "END TIME" },
    { content: "ELAPSED" },
    { content: "ACTIONS" },
  ];
  let rows: RowType[] = [];
  let spanMobile: RowType[] = [];
  let isInProgress = false;
  if (currentPractice.drills) {
    const startTimestampsMessages = {
      ...periodsValidation[PERIOD_START_LABEL],
    };
    const endTimestampsMessages = { ...periodsValidation[PERIOD_END_LABEL] };
    //@ts-ignore
    rows = currentPractice.drills.map((drill, index) => {
      const startButtonDisabled =
        index > 0 &&
        currentPractice.drills[index - 1].endTs > new Date().getTime();
      const participants = participantsByPeriod[index] || {
        included: 0,
        total: 0,
        all: false,
      };
      const periodInProgress =
        index === currentDrillIndex && drill.state === "START";
      if (periodInProgress) {
        periodOpen = index + 1;
      }
      //@ts-ignore
      isInProgress |=
        !editable &&
        (DateFns.isToday(currentPractice.start) ||
          DateFns.isPast(currentPractice.start)) &&
        !(
          index === currentDrillIndex &&
          !currentPractice.drills[currentDrillIndex].state
        ) &&
        index === currentDrillIndex;
      return {
        columns: [
          {
            content: index + 1,
            class: "t-ll",
            style: { padding: "19px 20px", textAlign: "center" },
            isDragHandle: true,
            print: (content) => (
              <Fragment>
                {!drill.startTs && editable && (
                  <img
                    src="/assets/dots.svg"
                    alt=""
                    height="15px"
                    style={{ marginTop: "-2px" }}
                  />
                )}
                <span
                  style={!drill.startTs && editable ? { float: "right" } : {}}
                >
                  {content}
                </span>
              </Fragment>
            ),
          },
          {
            content: drill.periodType ? drill.periodType : "",
            class: "t-ll",
            print: (content) => (
              <PeriodTypeInlineMultiSelector
                changeItemsSelected={(values) =>
                  handlePeriodTypesChange(
                    values!.map((value) => value.value),
                    drill.drillId as number
                  )
                }
                selectedItems={content === "" ? [] : content.split(",")}
                league={currentPractice.league}
                editable={editable}
                id={JSON.stringify(drill.periodType) + index}
                teamId={meta.homeTeam.teamId}
              />
            ),
          },
          {
            content: drill.drillType ? drill.drillType : "",
            class: "t-ll tt-qq",
            style: {
              padding: "19px 20px",
              color: periodInProgress ? "rgb(0, 124, 176)" : null,
              wordBreak: 'break-word'
            },
            print: (content) => (
              <EditableField
                itemId={drill.drillId as number}
                editable={editable}
                text={content}
                handleChange={handlePeriodNameChange}
              />
            ),
          },
          {
            content: drill.roofType ? drill.roofType : practice.rooftype,
            class: "t-ll",
            print: (content) =>
              editable ? (
                <div className="manage-p-table">
                  <Dropdown
                    className="inte-00 field-type-dropdown "
                    onSelect={(event) => {
                      noSave(true);
                      handlePeriodRoofType(event, drill.drillId);
                    }}
                  >
                    <Dropdown.Toggle id="dropdown-basic" disabled={!editable}>
                      {text_truncate(drill.roofType, 13) ||
                        text_truncate(content, 13)}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      {FIELD_TYPE.types.map((field, index) => (
                        <Dropdown.Item eventKey={field} key={field}>
                          {field}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              ) : (
                content
              ),
          },
          {
            content: !!drill.startTs ? drill.startTs : "",
            class: "t-ll",
            style: { padding: "19px 10px" },
            print: (content) => (
              <Fragment>
                {content === "" && !isPastPractice ? (
                  content
                ) : (
                  <EditableTime
                    index={index}
                    editable={editable}
                    time={content}
                    handleChange={(
                      ...args: [number, number, number, boolean, number?]
                    ) => handlePeriodsTimeChange(PERIOD_START_LABEL, ...args)}
                    timeIsWrong={!!startTimestampsMessages[index]}
                    rejectionMessage={startTimestampsMessages[index]}
                  />
                )}
              </Fragment>
            ),
          },
          {
            content: !!drill.endTs ? drill.endTs : "",
            class: "t-ll",
            style: { padding: "19px 10px" },
            print: (content) => {
              return (
                <Fragment>
                  {content === "" && !isPastPractice ? (
                    content
                  ) : (
                    <EditableTime
                      index={index}
                      editable={editable && !periodInProgress}
                      time={content}
                      handleChange={(
                        ...args: [number, number, number, boolean, number?]
                      ) => handlePeriodsTimeChange(PERIOD_END_LABEL, ...args)}
                      timeIsWrong={!!endTimestampsMessages[index]}
                      rejectionMessage={endTimestampsMessages[index]}
                    />
                  )}
                </Fragment>
              );
            },
          },
          {
            content: "",
            class: "t-ll",
            style: { padding: "19px 20px" },
            print: () => getTableEllapsedData(drill, index),
          },
          {
            content: "",
            class: "t-ll",
            style: {
              padding: "19px 10px",
              textAlign: "center",
            },
            print: () => (
              <Fragment>
                <span>{`(${participants.included} / ${participants.total}) ${
                  participants.all ? "All" : ""
                }`}</span>
                {editable && drill.session_id && (
                  <button
                    className={"btn-exclude-players-by-period"}
                    onClick={() => {
                      onOpenExcludePlayersModal(
                        Number(drill.session_id),
                        drill.drillType || ""
                      );
                    }}
                    style={{ paddingLeft: "5px" }}
                  >
                    <i
                      className="mdi mdi-account-cancel-outline"
                      style={{
                        color: "#D6151C",
                        fontSize: "20px",
                        verticalAlign: "middle",
                      }}
                    ></i>
                  </button>
                )}
              </Fragment>
            ),
          },
          {
            style: {
              color: "#4F9300",
              fontWeight: "bold",
              fontSize: "13px",
              padding: "19px 10px",
            },
            print: () => (
              <Fragment>
                <DrillProgress drill={drill} />
                {editable && drill.state !== "START" ? (
                  <button
                    className="trash-00"
                    onClick={() => handleDeletePeriod(index)}
                  >
                    <img src="/assets/trash.svg" alt="" height="20px" />
                  </button>
                ) : (
                  ""
                )}
                {!editable &&
                  (DateFns.isToday(currentPractice.start) ||
                    DateFns.isPast(currentPractice.start)) &&
                  (index === currentDrillIndex &&
                  !currentPractice.drills[currentDrillIndex].state ? (
                    <Button
                      className="btn-green"
                      onClick={() => {
                        setStarted(true);
                        setEnded(false);
                        handleStartPeriod(index);
                      }}
                      style={
                        started || startButtonDisabled
                          ? {
                              opacity: 0.5,
                              cursor: "default",
                              pointerEvents: "none",
                              color: "#FFFFFF",
                            }
                          : {}
                      }
                      disabled={startButtonDisabled}
                    >
                      Start Period
                    </Button>
                  ) : index === currentDrillIndex ? (
                    currentPractice.drills[index + 1] ? (
                      <>
                        <Button
                          className="btn-skip-period"
                          onClick={() => {
                            handleEndPeriod(index, true);
                          }}
                          style={
                            ended || updateQueue.queue.length > 0
                              ? {
                                  opacity: 0.5,
                                  cursor: "default",
                                  pointerEvents: "none",
                                }
                              : {}
                          }
                        >
                          <i className="mdi mdi-skip-next"></i>
                        </Button>
                        <Button
                          className="btn-orange"
                          onClick={() => {
                            setEnded(true);
                            setStarted(false);
                            handleEndPeriod(index, false);
                            setIsInProgressPeriod(false);
                          }}
                          style={
                            ended || updateQueue.queue.length > 0
                              ? {
                                  opacity: 0.5,
                                  cursor: "default",
                                  pointerEvents: "none",
                                }
                              : {}
                          }
                        >
                          End Period
                        </Button>
                      </>
                    ) : (
                      <Button
                        className="btn-orange"
                        onClick={() => {
                          setEnded(true);
                          setStarted(false);
                          handleEndPeriod(index, false);
                          setIsInProgressPeriod(false);
                        }}
                        style={
                          ended || updateQueue.queue.length > 0
                            ? {
                                opacity: 0.5,
                                cursor: "default",
                                pointerEvents: "none",
                              }
                            : {}
                        }
                      >
                        End Period
                      </Button>
                    )
                  ) : (
                    ""
                  ))}
              </Fragment>
            ),
          },
        ],
        style: { background: periodInProgress ? "#F0F6FA" : null },
        draggable: !drill.startTs,
        id: index,
      };
    });

    //MOBILE TABLE DATA
    //@ts-ignore
    spanMobile = currentPractice.drills.map((drill, index) => {
      const startButtonDisabled =
        index > 0 &&
        currentPractice.drills[index - 1].endTs > new Date().getTime();
      const periodInProgress =
        index === currentDrillIndex && drill.state === "START";
      //@ts-ignore
      isInProgress |=
        !editable &&
        (DateFns.isToday(currentPractice.start) ||
          DateFns.isPast(currentPractice.start)) &&
        !(
          index === currentDrillIndex &&
          !currentPractice.drills[currentDrillIndex].state
        ) &&
        index === currentDrillIndex;
      return {
        columns: [
          {
            content: drill.periodType ? drill.periodType : "",
            print: (content) => (
              <PeriodTypeInlineMultiSelector
                changeItemsSelected={(values) =>
                  handlePeriodTypesChange(
                    values!.map((value) => value.value),
                    drill.drillId as number
                  )
                }
                selectedItems={content === "" ? [] : content.split(",")}
                league={currentPractice.league}
                editable={editable}
                id={JSON.stringify(drill.periodType) + index}
                teamId={meta.homeTeam.teamId}
              />
            ),
          },
          {
            content: drill.roofType ? drill.roofType : currentPractice.rooftype,
            print: (content) =>
              editable ? (
                <div className="period-type-simple-multi-selector">
                  <div className="manage-p-table">
                    <Dropdown
                      className="inte-00 field-type-dropdown"
                      onSelect={(event) => {
                        noSave(true);
                        handlePeriodRoofType(event, drill.drillId);
                      }}
                    >
                      <Dropdown.Toggle id="dropdown-basic" disabled={!editable}>
                        {drill.roofType || content}
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        {FIELD_TYPE.types.map((field, index) => (
                          <Dropdown.Item eventKey={field} key={field}>
                            {field}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                </div>
              ) : (
                content
              ),
          },
          {
            content: !!drill.startTs ? drill.startTs : "",
            print: (content) => (
              <Fragment>
                {content === "" && !isPastPractice ? (
                  content
                ) : (
                  <EditableTime
                    index={index}
                    editable={editable}
                    time={content}
                    handleChange={(
                      ...args: [number, number, number, boolean, number?]
                    ) => handlePeriodsTimeChange(PERIOD_START_LABEL, ...args)}
                    timeIsWrong={!!startTimestampsMessages[index]}
                    rejectionMessage={startTimestampsMessages[index]}
                  />
                )}
              </Fragment>
            ),
          },
          {
            content: !!drill.endTs ? drill.endTs : "",
            print: (content) => {
              return (
                <Fragment>
                  {content === "" && !isPastPractice ? (
                    content
                  ) : (
                    <EditableTime
                      index={index}
                      editable={editable && !periodInProgress}
                      time={content}
                      handleChange={(
                        ...args: [number, number, number, boolean, number?]
                      ) => handlePeriodsTimeChange(PERIOD_END_LABEL, ...args)}
                      timeIsWrong={!!endTimestampsMessages[index]}
                      rejectionMessage={endTimestampsMessages[index]}
                    />
                  )}
                </Fragment>
              );
            },
          },
          {
            content: "",
            print: () => getTableEllapsedData(drill, index),
          },
          {
            style: {
              color: "#4F9300",
              fontWeight: "bold",
              fontSize: "13px",
            },
            print: () => (
              <Fragment>
                {editable && drill.state !== "START" ? (
                  <button
                    className="trash-00"
                    onClick={() => handleDeletePeriod(index)}
                  >
                    <img src="/assets/trash.svg" alt="" height="20px" />
                  </button>
                ) : (
                  ""
                )}
                {!editable &&
                  (DateFns.isToday(currentPractice.start) ||
                    DateFns.isPast(currentPractice.start)) &&
                  (index === currentDrillIndex &&
                  !currentPractice.drills[currentDrillIndex].state ? (
                    <Button
                      className="btn-green"
                      onClick={() => {
                        setStarted(true);
                        setEnded(false);
                        handleStartPeriodMobile(index);
                      }}
                      style={
                        started || startButtonDisabled
                          ? {
                              opacity: 0.5,
                              cursor: "default",
                              pointerEvents: "none",
                              color: "#FFFFFF",
                            }
                          : {}
                      }
                      disabled={startButtonDisabled}
                    >
                      Start Period
                    </Button>
                  ) : index === currentDrillIndex ? (
                    currentPractice.drills[index + 1] ? (
                      <>
                        <Button
                          className="btn-skip-period"
                          onClick={() => {
                            handleEndPeriod(index, true);
                          }}
                          style={
                            ended || updateQueue.queue.length > 0
                              ? {
                                  opacity: 0.5,
                                  cursor: "default",
                                  pointerEvents: "none",
                                }
                              : {}
                          }
                        >
                          <i className="mdi mdi-skip-next"></i>
                        </Button>
                        <Button
                          className="btn-orange"
                          onClick={() => {
                            setEnded(true);
                            setStarted(false);
                            handleEndPeriod(index, false);
                            setIsInProgressPeriod(false);
                          }}
                          style={
                            ended || updateQueue.queue.length > 0
                              ? {
                                  opacity: 0.5,
                                  cursor: "default",
                                  pointerEvents: "none",
                                }
                              : {}
                          }
                        >
                          End Period
                        </Button>
                      </>
                    ) : (
                      <Button
                        className="btn-orange"
                        onClick={() => {
                          setEnded(true);
                          setStarted(false);
                          handleEndPeriod(index, false);
                          setIsInProgressPeriod(false);
                        }}
                        style={
                          ended || updateQueue.queue.length > 0
                            ? {
                                opacity: 0.5,
                                cursor: "default",
                                pointerEvents: "none",
                              }
                            : {}
                        }
                      >
                        End Period
                      </Button>
                    )
                  ) : (
                    ""
                  ))}
              </Fragment>
            ),
          },
          {
            print: () => (
              <Fragment>
                <DrillProgress
                  drill={drill}
                  editable={editable}
                  isMobile={isMobile}
                />
              </Fragment>
            ),
          },
          {
            content: editable
              ? drill.drillType
                ? drill.drillType
                : ""
              : drill.drillType
              ? text_truncate(drill.drillType, 18)
              : "",
            style: { color: periodInProgress ? "rgb(0, 124, 176)" : null },
            print: (content) => (
              <EditableField
                itemId={drill.drillId as number}
                editable={editable}
                text={content}
                handleChange={handlePeriodNameChange}
              />
            ),
          },
        ],
        style: { background: periodInProgress ? "#F0F6FA" : null },
        draggable: !drill.startTs,
        id: index,
      };
    });
  }
  useEffect(() => {
    setIsInProgressPeriod(isInProgress && !ended);
  });
  return editable ? (
    isMobile ? (
      <div className="collapse-manage">
        <PeriodsTableMobile rows={spanMobile} indexLive={periodOpen} />
        <div
          onClick={handleAddPeriod}
          style={{
            background: "#F5F5F5",
            zIndex: 7,
            display: editable ? undefined : "none",
            height: "36px",
            width: "100%",
            fontSize: "25px",
            textAlign: "center",
            fontWeight: 500,
            color: "#007ABA",
            cursor: "pointer",
            // boxShadow: '0px 0px 3px rgba(0,0,0,0.3)',
            fontFamily: "Roboto",
            position: "absolute",
            bottom: "0",
          }}
        >
          <label className="ll-m-01" style={{}}>
            +
          </label>
          <label
            className="ll-m-02"
            style={{
              fontSize: "14px",
              lineHeight: "20px",
              cursor: "pointer",
              position: "absolute",
              top: "14px",
              left: "calc(45% + 26px)",
            }}
          >
            Add Period
          </label>
        </div>
      </div>
    ) : (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          {/* @ts-ignore */}
          <DraggableTable
            headers={headers}
            rows={rows}
            onChangeRows={onChangeRows}
          />
        </div>
        <div
          onClick={handleAddPeriod}
          style={{
            background: "#F5F5F5",
            height: "48px",
            width: "100%",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            fontWeight: 500,
            color: "#007ABA",
            cursor: "pointer",
            fontFamily: "Roboto",
          }}
        >
          <span style={{ fontSize: "36px", lineHeight: "20px" }}>+</span>
          <span style={{ fontSize: "14px", lineHeight: "20px" }}>
            Add Period
          </span>
        </div>
      </div>
    )
  ) : isMobile ? (
    <div className="collapse-manage">
      {meta.type === "livePractice" && isInProgress ? (
        <Loader forceScope={"PERIODS_MOBILE"}>
          <PeriodsTableMobile rows={spanMobile} indexLive={periodOpen} />
        </Loader>
      ) : (
        <PeriodsTableMobile rows={spanMobile} />
      )}
    </div>
  ) : (
    <>
      <Table headers={headers} rows={rows} />
    </>
  );
};
export default PeriodsTable;
